import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  MenuDivider,
  Text,
  Flex,
  Tag,
  BoxProps,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { FaChevronDown } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";

import TokenTags from "@components/TokenTags";
import {
  LSTIconMapping,
  LstOptions,
  getLSTOptionsForChain,
} from "@constants/common";

import { updateUserData } from "store";
import useDappChain from "@hooks/useDappChain";
import useUserType from "@hooks/useUserType";
import { formatNumber, trackEvent } from "utils";
import { TOKEN_SELECTION } from "@constants/analytics";
import useDappConfig from "@hooks/useDappConfig";
import { ReactNode } from "react";
import useBufferPool from "@hooks/useBufferPool";

const MenuItemProps = {
  p: "6px 8px",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: "400",
};

const SelectedMenuItemProps = {
  ...MenuItemProps,
  bg: "#F5F5F7",
  color: "#142528",
  fontWeight: "600",
  lineHeight: "20px",
  letterSpacing: "-0.70px",
};

interface LstTokenSelectProps extends BoxProps {
  hideTags?: boolean;
  hideETH?: boolean;
  forUnstake?: boolean;
  title?: string;
  isVaultPage?: boolean;
  isYieldVaultPage?: boolean;
  rightSection?: ReactNode;
}

const LstTokenSelect = ({
  hideTags = false,
  hideETH = false,
  forUnstake = false,
  title = "Select the asset",
  isVaultPage = false,
  isYieldVaultPage = false,
  rightSection,
  ...rest
}: LstTokenSelectProps) => {
  const { selectedERC20, selectedERC20Vault, selectedERC20YieldVault } =
    useSelector((state: any) => state.user);

  const { bufferPool } = useBufferPool();

  const { chain } = useDappChain();
  const dispatch = useDispatch();
  const { config } = useDappConfig();

  const { isStaderLedgerUser } = useUserType();

  const options = getLSTOptionsForChain(
    chain.id,
    hideETH || (!forUnstake && !config.nativeToken.isRestakeSupported),
    isStaderLedgerUser,
    isVaultPage
  );

  const sortKey = forUnstake ? "unstakePriority" : "priority";

  const sortedOptions = options.sort(
    (a: any, b: any) => (a?.[sortKey] || 0) - (b?.[sortKey] || 0)
  );

  const getSelectedAsset = () => {
    if (isVaultPage) return selectedERC20Vault;
    if (isYieldVaultPage) return selectedERC20YieldVault;

    return selectedERC20;
  };

  const selectedAsset = getSelectedAsset();

  const getKey = () =>
    (isVaultPage && "selectedERC20Vault") ||
    (isYieldVaultPage && "selectedERC20YieldVault") ||
    "selectedERC20";

  const handleSelection = (option: LstOptions) => {
    dispatch(updateUserData({ [getKey()]: option.key }));
    trackEvent(TOKEN_SELECTION, { token: option.key });
  };

  return (
    <Box w="100%" mb="2.5rem" {...rest}>
      <Flex w="100%" justifyContent="space-between">
        <Text mb="12px">{title}</Text>
        {rightSection && rightSection}
      </Flex>
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<FaChevronDown />}
          w="100%"
          textAlign="left"
          className="menuButtonStake"
          p={{ base: "16px", md: "16px 16px 16px 24px" }}
          h="auto"
          borderRadius="16px"
          bg="#E4F8F8"
          color="textPrimary"
          _hover={{
            bg: "#E4F8F8",
          }}
          _active={{
            bg: "#E4F8F8",
          }}
          fontWeight="500"
          fontSize={{ base: "14px", md: "18px" }}
          letterSpacing="-0.70px"
        >
          <Flex alignItems="center" gap="8px">
            {LSTIconMapping[selectedAsset] && (
              <BaseImage
                width={24}
                height={24}
                alt="lst"
                src={LSTIconMapping[selectedAsset]}
              />
            )}
            <Flex alignItems="center" justify="space-between" flex={1} gap={2}>
              <Box flex={1}>{selectedAsset}</Box>

              {!hideTags && <TokenTags token={selectedAsset} />}
              {forUnstake && (
                <Text className="text-base" letterSpacing="-0.1px">
                  {formatNumber(bufferPool[selectedAsset] || 0, false, 2)}
                </Text>
              )}
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList
          transformOrigin="top !important"
          w={{
            base: "340px",
            sm: "100%",
          }}
          p="8px"
          border="1px solid #AEDFCA"
          bg="backgroundCard"
          fontSize="14px"
          fontWeight="400"
        >
          {sortedOptions.map((option, index) => {
            if (option.key === "divider") {
              return <MenuDivider my="12px" key={index} />;
            } else {
              const props =
                option.key === selectedAsset
                  ? { ...SelectedMenuItemProps }
                  : { ...MenuItemProps };
              return (
                <MenuItem
                  {...props}
                  isDisabled={option?.isDisabled}
                  _disabled={{
                    cursor: "not-allowed",
                    color: "textTertiary",
                  }}
                  padding={2}
                  marginBottom={1}
                  key={index}
                  onClick={() => handleSelection(option)}
                >
                  <Flex
                    justifyContent="space-between"
                    w="100%"
                    alignItems="center"
                  >
                    <Flex alignItems="center" gap="8px" flex={1}>
                      {LSTIconMapping[option.key] && (
                        <BaseImage
                          width={24}
                          height={24}
                          alt="lst"
                          src={LSTIconMapping[option.key]}
                        />
                      )}
                      <Flex
                        justify="space-between"
                        flex={1}
                        alignItems="center"
                        gap={2}
                      >
                        <Box flex={1} fontSize={18}>
                          {option.title}
                        </Box>

                        {!hideTags && (
                          <TokenTags token={option.key} maxW="100%" />
                        )}
                      </Flex>
                    </Flex>
                    {!hideTags && option.tag && (
                      <Tag
                        bg="backgroundCanvas"
                        color="textTertiary"
                        fontWeight="600"
                        fontSize="10px"
                        p="2px 4px"
                        borderRadius="4px"
                        lineHeight="12px"
                        letterSpacing="0.4px"
                      >
                        {option.tag}
                      </Tag>
                    )}
                    {forUnstake &&
                    (bufferPool[option.key] || bufferPool[option.key] === 0) ? (
                      <Text className="text-base" letterSpacing="-0.1px">
                        {formatNumber(bufferPool[option.key] || 0, false, 2)}
                      </Text>
                    ) : null}
                  </Flex>
                </MenuItem>
              );
            }
          })}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default LstTokenSelect;
